// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useRef, useState } from 'react';

import type { ItemData } from '@aps_sdk/data-management';
import {
  type ProjectId,
  type AutodeskCloudModel,
  useEvolvePaginatedGetAll,
  type GetPaginatedAllOpts,
} from '@evolvemep/api-ts';

import { useAutodeskAuth } from 'components/Autodesk/auth/useAutodeskAuth';
import { isNotNil, isNil } from 'helpers/isNotNil';

type Opts = {
  hideInaccessible?: boolean;
};

export const useAutodeskModels = (projectId: ProjectId | undefined, { hideInaccessible }: Opts = {}) => {
  const { dataClient, getAutodeskProject } = useAutodeskAuth();
  const defaultOpts = useMemo<GetPaginatedAllOpts<'shop/autodesk/autodeskModel'>>(
    () => ({
      lazy: isNil(projectId),
      defaultConfig: {
        params: {
          projectId,
        },
      },
    }),
    [projectId],
  );

  // TODO: Update this to be paginated, instead.
  const {
    data: models,
    // This tracks the loading state of the Autodesk models from our backend.
    loading: loadingEvolveCloudModels,
    apiCall: refetch,
    setDefaultOpts,
  } = useEvolvePaginatedGetAll(`shop/autodesk/autodeskModel`, defaultOpts);

  useEffect(() => {
    setDefaultOpts(defaultOpts);
  }, [defaultOpts, setDefaultOpts]);

  const [modelCache, setModelCache] = useState<Record<AutodeskCloudModel['urn'], ItemData | null>>({});
  const modelCacheRef = useRef(modelCache);
  useEffect(() => {
    modelCacheRef.current = modelCache;
  }, [modelCache]);

  // Tracks the loading state of the Autodesk models from Autodesk's data client.
  const [loadingAutodeskModels, setLoadingAutodeskModels] = useState(false);
  useEffect(() => {
    if (isNil(models)) return undefined;
    let cancelled = false;
    setLoadingAutodeskModels(true);
    void Promise.all(
      models.map(async (model) => {
        if (!(model.urn in modelCacheRef.current)) {
          const project = await getAutodeskProject(model.autodeskHubId, model.autodeskProjectId);
          if (cancelled) return;
          if (isNotNil(project)) {
            await dataClient
              .getItem(model.autodeskProjectId, model.urn)
              // Catch any error and set the model to `null`
              // which will show a "No access" error to the user
              .catch((err) => {
                // oxlint-disable-next-line no-console
                console.error(err);
                return null;
              })
              .then((res) => {
                if (!cancelled) {
                  setModelCache((c) => ({
                    ...c,
                    [model.urn]: res?.data ?? null,
                  }));
                }
              });
          } else {
            setModelCache((c) => ({
              ...c,
              [model.urn]: null,
            }));
          }
        }
      }),
    ).finally(() => {
      if (!cancelled) setLoadingAutodeskModels(false);
    });
    return () => {
      cancelled = true;
    };
  }, [dataClient, models, getAutodeskProject]);

  const filteredModels = useMemo(() => {
    // Switching projects triggers a refetch, but while that refetch is in flight our model list still contains the old project's models.
    // Filter out any mismatches.
    const projectModels = models?.filter((m) => m.projectId === projectId);
    return hideInaccessible ? projectModels?.filter((m) => isNotNil(modelCache[m.urn])) : projectModels;
  }, [projectId, hideInaccessible, modelCache, models]);

  return {
    models: filteredModels,
    modelCache,
    loading: loadingEvolveCloudModels || loadingAutodeskModels,
    refetch,
  };
};
